/**
 * The frontend framework choice, as the prompt form needs to present it.
 *
 * Presentation-only data lives here rather than in either skin because both
 * skins offer the same choice and must agree on what it means — the option
 * list, the default, and the one-line description under each. A skin decides
 * how the control LOOKS (a segmented pair in `classic`, something egg-shaped in
 * `hatch`); it does not get to decide what the options are, or one skin quietly
 * ships a third framework the API cannot build.
 *
 * The union itself comes from `@hatch/shared`, so adding a framework is one
 * edit in the contract and both skins pick it up.
 *
 * This is the smallest thing that is genuinely a choice in a product whose
 * promise is "a description is enough" — see the note in promptGallery.ts about
 * not letting the form become a set of options. The framework is defensible
 * where a style picker would not be: it changes what the generated repo IS, not
 * how Hatch behaves, and there is no way to infer it from the prompt text.
 */

import { DEFAULT_FRAMEWORK, FRAMEWORKS, type Framework } from "@hatch/shared";

export interface FrameworkOption {
  value: Framework;
  /** Button text. Short — it sits next to the submit control. */
  label: string;
  /** One line, for a title attribute or helper text. Never a feature list. */
  hint: string;
}

/**
 * Ordered as the control renders them, default first.
 *
 * Derived from `FRAMEWORKS` rather than written out, so a framework added to
 * the contract without a label here fails the build instead of silently
 * disappearing from the picker.
 */
const LABELS: Record<Framework, Omit<FrameworkOption, "value">> = {
  react: { label: "React", hint: "React 19 with shadcn/ui components" },
  svelte: { label: "Svelte", hint: "Svelte 5 with shadcn-svelte components" },
};

export const FRAMEWORK_OPTIONS: FrameworkOption[] = FRAMEWORKS.map((value) => ({
  value,
  ...LABELS[value],
}));

/**
 * Whether the prompt form shows the picker at all. Currently false: every
 * Svelte build fails in `npm run build`, because `svelte-check` type-checks
 * `xano/` too and the agent writes `auth("id")` without importing `auth` — the
 * cheatsheet's one worked import list does not name it. Offering a choice that
 * cannot finish is worse than offering none, so the form sends
 * `DEFAULT_FRAMEWORK` and says nothing.
 *
 * Hidden, not removed. `svelte` stays in the contract and the API still builds
 * it on request: SMS, the CLI and a booth redeploy of an older session all
 * carry a framework Hatch chose before this, and the option list above is what
 * the done screen reads a label out of. Flip this back once the import lands.
 */
export const FRAMEWORK_PICKER_VISIBLE = false;

export { DEFAULT_FRAMEWORK, type Framework };
